import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Testimonials.css';

// Import client reference images
import tradeJournalImg from '../assets/TradeJournal.webp';
import boatAppImg from '../assets/Boat App.webp';
import mammacitaImg from '../assets/Mammacita.webp';
import sondermomentImg from '../assets/Sondermoment.webp';
import mozartSecurityImg from '../assets/Mozart Security.webp';

const Testimonials = () => {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const testimonialsRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Animation on scroll
  useEffect(() => {
    const section = sectionRef.current;
    const title = titleRef.current;
    const testimonials = testimonialsRef.current;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          section.classList.add('visible');
          setTimeout(() => title.classList.add('visible'), 200);
          setTimeout(() => testimonials.classList.add('visible'), 400);
          
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });
    
    if (section) {
      observer.observe(section);
    }
    
    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);
  
  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % 5);
    }, 7000);
    
    return () => clearInterval(interval);
  }, []);
  
  const handleDotClick = (index) => {
    setActiveIndex(index);
  };

  return (
    <section className="testimonials" ref={sectionRef}>
      <div className="testimonials-background">
        <div className="testimonials-shape testimonials-shape-1"></div>
        <div className="testimonials-shape testimonials-shape-2"></div>
      </div>
      
      <div className="container">
        <div className="testimonials-content">
          <h2 className="testimonials-title" ref={titleRef}>
            {t('testimonialsTitle')}
          </h2>
          
          <div className="testimonials-slider" ref={testimonialsRef}>
            <div className="testimonials-track" style={{ transform: `translateX(-${activeIndex * 100}%)` }}>
              {/* Aktienhandelsplatform Testimonial */}
              <div className="testimonial-item">
                <div className="testimonial-image-oval large" >
                  <picture>
                  <source srcSet={tradeJournalImg} type="image/webp" />
                  <img src={tradeJournalImg} alt={t('testimonialCompany1Alt')} className="testimonial-image" loading="lazy" />
                </picture>
                </div>
                <div className="testimonial-quote redesigned">
                  <span className="quote-mark">"</span>
                  {t('testimonialQuote1')}
                </div>
                <div className="testimonial-company redesigned">
                  {t('testimonialCompany1')}
                </div>
              </div>
              {/* Boat App Testimonial */}
              <div className="testimonial-item">
                <div className="testimonial-image-oval large" >
                  <picture>
                  <source srcSet={boatAppImg} type="image/webp" />
                  <img src={boatAppImg} alt={t('testimonialCompany2Alt')} className="testimonial-image" loading="lazy" />
                </picture>
                </div>
                <div className="testimonial-quote redesigned">
                  <span className="quote-mark">"</span>
                  {t('testimonialQuote2')}
                </div>
                <div className="testimonial-company redesigned">
                  {t('testimonialCompany2')}
                </div>
              </div>
              {/* Hebamme Testimonial */}
              <div className="testimonial-item">
                <div className="testimonial-image-oval large" >
                  <picture>
                  <source srcSet={mammacitaImg} type="image/webp" />
                  <img src={mammacitaImg} alt={t('testimonialCompany3Alt')} className="testimonial-image" loading="lazy" />
                </picture>
                </div>
                <div className="testimonial-quote redesigned">
                  <span className="quote-mark">"</span>
                  {t('testimonialQuote3')}
                </div>
                <div className="testimonial-company redesigned">
                  {t('testimonialCompany3')}
                </div>
              </div>
              {/* Kartenspiel Testimonial */}
              <div className="testimonial-item">
                <div className="testimonial-image-oval large" >
                  <picture>
                  <source srcSet={sondermomentImg} type="image/webp" />
                  <img src={sondermomentImg} alt={t('testimonialCompany4Alt')} className="testimonial-image" loading="lazy" />
                </picture>
                </div>
                <div className="testimonial-quote redesigned">
                  <span className="quote-mark">"</span>
                  {t('testimonialQuote4')}
                </div>
                <div className="testimonial-company redesigned">
                  {t('testimonialCompany4')}
                </div>
              </div>
              {/* Sicherheitsdienst Testimonial */}
              <div className="testimonial-item">
                <div className="testimonial-image-oval large" >
                  <picture>
                  <source srcSet={mozartSecurityImg} type="image/webp" />
                  <img src={mozartSecurityImg} alt={t('testimonialCompany5Alt')} className="testimonial-image" loading="lazy" />
                </picture>
                </div>
                <div className="testimonial-quote redesigned">
                  <span className="quote-mark">"</span>
                  {t('testimonialQuote5')}
                </div>
                <div className="testimonial-company redesigned">
                  {t('testimonialCompany5')}
                </div>
              </div>
            </div>
            <div className="testimonial-dots">
              {[0, 1, 2, 3, 4].map((index) => (
                <button 
                  key={index} 
                  className={`testimonial-dot ${activeIndex === index ? 'active' : ''}`}
                  onClick={() => handleDotClick(index)}
                  aria-label={`View testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
